import type { BoardPost } from '@talkofthetown/shared';

/** Sponsored whispers take at most this share of the whispers on screen (and at least one). */
export const SPONSORED_SHARE = 1 / 3;

/** How many of `room` whispers on screen may be sponsored. */
export const sponsoredRoom = (room: number) => Math.max(1, Math.floor(room * SPONSORED_SHARE));

/** Where each list is up to: whispers take turns, sponsored ones among themselves. */
export interface WhisperTurns {
  whisper: number;
  sponsored: number;
}

/** The first post from `list`, starting at `from`, not already showing; and where to go on from. */
function firstFree(
  list: readonly BoardPost[],
  from: number,
  showing: ReadonlySet<string>,
): { post: BoardPost; next: number } | null {
  for (let i = 0; i < list.length; i++) {
    const at = (from + i) % list.length;
    if (!showing.has(list[at]!.id)) return { post: list[at]!, next: (at + 1) % list.length };
  }
  return null;
}

/**
 * The next whisper to show. Sponsored whispers come first while they're under their share of the
 * screen (so they come round more often), then the others in turn.
 */
export function nextWhisper(
  whispers: readonly BoardPost[],
  sponsored: readonly BoardPost[],
  showing: readonly BoardPost[],
  room: number,
  turns: WhisperTurns,
): { post: BoardPost; turns: WhisperTurns } | null {
  const ids = new Set(showing.map((p) => p.id));
  const paidShowing = showing.filter((p) => p.sponsored).length;
  if (paidShowing < sponsoredRoom(room)) {
    const paid = firstFree(sponsored, turns.sponsored, ids);
    if (paid) return { post: paid.post, turns: { ...turns, sponsored: paid.next } };
  }
  const next = firstFree(whispers, turns.whisper, ids);
  return next ? { post: next.post, turns: { ...turns, whisper: next.next } } : null;
}

/** With reduced motion, the whispers that sit still: the sponsored share first, then the rest. */
export function stillWhispers(
  whispers: readonly BoardPost[],
  sponsored: readonly BoardPost[],
  room: number,
): BoardPost[] {
  const paid = sponsored.slice(0, sponsoredRoom(room));
  return [...paid, ...whispers].slice(0, room);
}
